import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  Button,
  Select,
  InputLabel,
  FormControl,
  SelectChangeEvent,
} from "@mui/material";
import Breadcrumb from "../../DefaultComponent/BreadCrumb";
import Spinner from "../../DefaultComponent/Spinner";
import ToastDefault from "../../DefaultComponent/Toast";
import { formatDate, calculateTotalPages } from "../../../utils/Helper";
import {
  ServiceChargeCreateAction,
  ServiceChargeListAction,
} from "../../../Redux/Actions/PaymentAction";
import CreateServiceCharge, {
  ServiceChargeCreate,
} from "./CreateServiceCharge/CreateServiceCharge";
import { toast } from "react-toastify";

export interface ServiceCharge {
  id: number;
  action_event: string;
  previous_master_service_charge: number | null;
  is_credit_charge: boolean;
  created_at: string;
  updated_at: string;
  content_type_id: number | null;
  unit_type: string;
  created_by: number;
  unit: string | null;
  updated_by: number | null;
  external_service_resource_id: number | null;
  unit_value: number | null;
  mark_del: boolean;
  feature_id: number;
  status: string;
  negative_deduction_allowed: boolean;
  credit_charge: number;
}

const initialFilters = {
  feature_id: "",
  action_event: "",
  content_type_id: "",
  external_service_resource_id: "",
  unit_type: "",
  is_credit_charge: "true",
};

const ServiceCharges: React.FC = () => {
  const dispatch = useDispatch<any>();
  const data = useSelector((state: any) => state.ServiceChargeListData);

  const [items, setItems] = useState<ServiceCharge[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage] = useState<number>(10);
  const [filters, setFilters] = useState(initialFilters);
  const [openForm, setOpenForm] = useState<boolean>(false);
  const CreateServiceChargeResponse = useSelector(
    (state: any) => state.CreateServiceChargeDara
  );

  const buildQuery = (pageNum: number, f: typeof initialFilters) => {
    const params: Record<string, any> = {
      page: pageNum,
      page_size: rowsPerPage,
      ...f,
    };
    Object.keys(params).forEach(
      (k) => (params[k] === "" || params[k] == null) && delete params[k]
    );
    return `?${new URLSearchParams(params).toString()}`;
  };

  const fetchItems = (pageNum = page, f = filters) => {
    setLoading(true);
    const qs = buildQuery(pageNum, f);
    dispatch(ServiceChargeListAction(qs)).finally(() => setLoading(false));
  };

  const applyFilters = () => {
    setPage(1);
    fetchItems(1, filters);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    fetchItems(1, initialFilters);
  };

  useEffect(() => {
    fetchItems();
  }, [dispatch, page]);

  useEffect(() => {
    if (
      CreateServiceChargeResponse?.status &&
      CreateServiceChargeResponse?.data
    ) {
      toast.success(CreateServiceChargeResponse?.data?.data?.message);
      fetchItems(1, initialFilters);
      setOpenForm(false);
    }
  }, [CreateServiceChargeResponse]);

  useEffect(() => {
    if (data?.data?.row) setItems(data.data.row);
  }, [data]);

  const handleFilterChange = (
    e:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateServiceCharge = (data: ServiceChargeCreate) => {
    dispatch(ServiceChargeCreateAction(data));
  };

  return (
    <>
      {loading && <Spinner />}
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Service Charges"
            breadcrumbs={[
              {
                label: "Service Charges",
                path: "/service-charges",
                isActive: true,
              },
            ]}
          />
        </div>

        <Box sx={{ m: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
          <TextField
            name="feature_id"
            label="Feature ID"
            value={filters.feature_id}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="action_event"
            label="Action Event"
            value={filters.action_event}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="content_type_id"
            label="Content Type ID"
            value={filters.content_type_id}
            onChange={handleFilterChange}
            size="small"
          />
          <TextField
            name="external_service_resource_id"
            label="External Resource ID"
            value={filters.external_service_resource_id}
            onChange={handleFilterChange}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="unit-type-label">Unit Type</InputLabel>
            <Select
              labelId="unit-type-label"
              name="unit_type"
              value={filters.unit_type}
              onChange={handleFilterChange}
              label="Unit Type"
            >
              <MenuItem value="TIME">Time</MenuItem>
              <MenuItem value="ACTION">Action</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="credit-label">Credit Charge</InputLabel>
            <Select
              labelId="credit-label"
              name="is_credit_charge"
              value={filters.is_credit_charge}
              onChange={handleFilterChange}
              label="Credit Charge"
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={applyFilters}>
            Apply Filters
          </Button>
          <Button variant="outlined" onClick={clearFilters}>
            Clear Filters
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenForm(true)}
          >
            Create Service Charge
          </Button>
        </Box>

        <Box sx={{ m: 2 }}>
          <TableContainer component={Paper}>
            <Table aria-label="Service Charges Table">
              <TableHead sx={{ backgroundColor: "#0555c8" }}>
                <TableRow>
                  <TableCell sx={{ color: "white" }}>S.No</TableCell>
                  <TableCell sx={{ color: "white" }}>Action Event</TableCell>
                  <TableCell sx={{ color: "white" }}>Feature ID</TableCell>
                  <TableCell sx={{ color: "white" }}>Credit Charge</TableCell>
                  <TableCell sx={{ color: "white" }}>Is Credit</TableCell>
                  <TableCell sx={{ color: "white" }}>CT ID</TableCell>
                  <TableCell sx={{ color: "white" }}>Unit Type</TableCell>
                  <TableCell sx={{ color: "white" }}>Unit</TableCell>
                  <TableCell sx={{ color: "white" }}>Unit Value</TableCell>
                  <TableCell sx={{ color: "white" }}>ExtR ID</TableCell>
                  <TableCell sx={{ color: "white" }}>Status</TableCell>
                  <TableCell sx={{ color: "white" }}>Created At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length > 0 ? (
                  items.map((item, idx) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {(page - 1) * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell>{item.action_event}</TableCell>
                      <TableCell>{item.feature_id}</TableCell>
                      <TableCell>{item.credit_charge}</TableCell>
                      <TableCell>
                        {item.is_credit_charge ? "Yes" : "No"}
                      </TableCell>
                      <TableCell>{item.content_type_id ?? "N/A"}</TableCell>
                      <TableCell>{item.unit_type}</TableCell>
                      <TableCell>{item.unit ?? "N/A"}</TableCell>
                      <TableCell>{item.unit_value ?? "N/A"}</TableCell>
                      <TableCell>
                        {item.external_service_resource_id ?? "N/A"}
                      </TableCell>
                      <TableCell>{item.status}</TableCell>
                      <TableCell>{formatDate(item.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      No Service Charges Found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <div className="pagination-style">
          <Stack spacing={2}>
            <Pagination
              count={calculateTotalPages(
                data?.data?.total_count || 0,
                rowsPerPage
              )}
              page={page}
              onChange={(e: any, newPage) => setPage(newPage)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Stack>
        </div>
      </div>
      <ToastDefault />
      <CreateServiceCharge
        open={openForm}
        onClose={() => setOpenForm(false)}
        onSubmit={handleCreateServiceCharge}
      />
    </>
  );
};

export default ServiceCharges;
